import { useState } from 'react';
import type { Role } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE, MENU_ITEMS } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { RoleFormModal } from './RoleFormModal.js';
import { useRoles } from './api.js';

const SECTION_BY_KEY = new Map(MENU_ITEMS.map((item) => [item.key, item.section]));

function permissionSummary(role: Role): string {
  const granted = role.menuPermissions.filter((permission) => permission.level !== 'none');
  if (granted.length === 0) {
    return 'No access';
  }
  const sections: string[] = [];
  for (const permission of granted) {
    const section = SECTION_BY_KEY.get(permission.key);
    if (section && !sections.includes(section)) sections.push(section);
  }
  const itemLabel = `${granted.length} item${granted.length === 1 ? '' : 's'}`;
  return `${itemLabel} · ${sections.join(', ')}`;
}

export function RolesPage(): JSX.Element {
  const { isAdmin } = useAuth();

  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);

  const query = useRoles({ page, pageSize: DEFAULT_PAGE_SIZE });

  const roles = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (role: Role): void => {
    setEditing(role);
    setFormOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Roles"
        description="Define permission sets that grant access to areas of the app."
        actions={
          isAdmin ? (
            <Button onClick={openCreate}>
              <Icon name="roles" className="h-4 w-4" />
              New role
            </Button>
          ) : undefined
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Permissions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={3}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={roles.length === 0}
              emptyTitle="No roles found"
              emptyDescription="Create your first role to get started."
              emptyAction={isAdmin ? <Button onClick={openCreate}>New role</Button> : undefined}
            >
              {roles.map((role) => (
                <tr
                  key={role.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(role)}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">{role.name}</td>
                  <td className="px-4 py-3">
                    <Badge tone={role.isSystem ? 'blue' : 'gray'}>
                      {role.isSystem ? 'System' : 'Custom'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{permissionSummary(role)}</td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && roles.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <RoleFormModal isOpen={formOpen} role={editing} onClose={() => setFormOpen(false)} />
    </div>
  );
}
